/* eslint-disable indent */
import Axios from 'axios'
import { sha512 } from 'js-sha512'
import { NextRouter } from 'next/router'
import qs from 'query-string'
import { toArray } from 'stringz'

import { FOUR_DEPTH_ADDRESSES, objectKeys } from '@local/utils'

import { ReviewGroupsMediaFilter, UserReviewGroupsSort } from '@web/gql/graphql'
import { BASE_PATH, IS_BROWSER, PHASE } from '@web/libs/constants'
import { Nullable, PathParam, StringParams } from '@web/types'

import links from './constants/links'
import routes from './constants/routes'

Axios.defaults.withCredentials = true

export const convertFileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.readAsDataURL(file)
    reader.onerror = reject
  })

export type NeloLoggerLevel = 'info' | 'warn' | 'error'

export const neloLogger = async (level: NeloLoggerLevel = 'error', message: any, metaData?: Record<string, any>) => {
  try {
    const browserLog = typeof window !== 'undefined' && {
      page: window?.location?.href,
      referer: window?.document?.referrer,
      ssr: IS_SSR,
    }

    const url = `${links.API_URL_ON_BROWSER}/nelo-logger`
    await Axios.post(url, {
      data: {
        level,
        message,
        ...browserLog,
        ...metaData,
      },
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    if (PHASE === 'development' || PHASE === 'qa') console.error(error)
  }
}

export const isHTMLElement = (element: Element | HTMLElement): element is HTMLElement => {
  return (element as HTMLElement).offsetLeft !== undefined
}

const LIMIT_EXPOSURE_NUMBER = 100000
const UNIT = 10000

export const numberToStringComma = (
  num: number,
  { limit, decimalPoint }: { limit: number; decimalPoint: number } = { limit: LIMIT_EXPOSURE_NUMBER, decimalPoint: 0 },
): string => {
  if (!num) {
    return '0'
  }

  if (num >= limit) {
    const decimalPointNumber = 10 ** decimalPoint
    const splitNumber = Math.floor((num / UNIT) * decimalPointNumber) / decimalPointNumber
    return `${splitNumber}만`
  }

  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const chunk = <T>(arr: T[], size: number): T[][] => {
  const length = arr == null ? 0 : arr.length
  if (!length || size < 1) {
    return []
  }
  let index = 0
  let resIndex = 0
  const result: T[][] = new Array(Math.ceil(length / size))

  while (index < length) {
    result[resIndex] = arr.slice(index, (index += size))
    resIndex += 1
  }
  return result
}

export const objectEntries = <T extends object, Key extends keyof T>(obj: T) => Object.entries(obj) as [Key, T[Key]][]

export const parseCategory = (category: Nullable<string>): Nullable<string> => {
  if (!category) {
    return null
  }

  if (category.includes('-')) {
    return category.split(/-/)[0]
  }

  const splitted = category.split(/>/)
  return splitted[splitted.length - 1]
}

export const exposingAddress = (address: Nullable<string>): Nullable<string> => {
  if (!address) {
    return null
  }

  const hasFourDepthAddress = FOUR_DEPTH_ADDRESSES.some(item => address.includes(item))
  const depth = hasFourDepthAddress ? 4 : 3

  return address.split(' ').slice(0, depth).join(' ')
}

/**
 * 값이 Nullish가 아님을 보장하는 type guard
 * usage) [undefined,100,2].filter(isNotNullish)
 */
export const isNotNullish = <T>(val: T | undefined | null): val is T => val !== undefined && val !== null

/**
 * deepCopy & 모든 객체에 __typename 제거
 */
export const deepOmitTypename = <T>(value: T): T => {
  if (typeof value !== 'object') return value

  // null
  if (value === null) return value

  // array
  if (Array.isArray(value)) return value.map(deepOmitTypename) as unknown as T

  // object
  return Object.entries(value).reduce((acc, [key, value]) => {
    if (key === '__typename') return acc
    return { ...acc, [key]: deepOmitTypename(value) }
  }, {} as T)
}

/**
 * 빈 배열인지 확인
 */
export const isEmptyArray = (arr: any[]) => {
  return arr.length === 0
}

export const IS_SSR = typeof window === 'undefined'

export const stringifyWithoutCyclicStructures = (obj: Object) => {
  const seen = new WeakSet()

  return JSON.stringify(obj, (_, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return undefined
      }
      seen.add(value)
    }

    return value
  })
}

/**
 * 여러 에러 타입에 대하여 string으로 에러메시지를 추출하는 함수
 * @param error 에러
 * @returns 여러 타입에 대응하여 뽑아낸 에러메시지
 */
export const extractErrorMessage = (error: unknown): string => {
  if (!error) return 'Error is undefined'

  if (typeof error !== 'object') return String(error)

  if (error instanceof Error) return `${error.message} ${error.stack}`

  return `Unknown error ${stringifyWithoutCyclicStructures(error)}`
}

export const truncateNumber = (inputNumber = 0): string => {
  const num = Number(inputNumber)

  if (Number.isNaN(num)) return '0'
  else if (num < 1000) {
    return num.toString()
  } else if (num < 10000) {
    return num.toLocaleString()
  } else if (num < 100000000) {
    return (num / 10000).toFixed(num % 10000 === 0 ? 0 : 1) + '만'
  } else {
    return (num / 100000000).toFixed(num % 100000000 === 0 ? 0 : 1) + '억'
  }
}

export const groupByKey = <V extends Record<string, any>>(
  arr: Array<V>,
  getKey: (element: V) => string,
): Record<string, V[]> => {
  const map = arr.reduce((acc, elem) => {
    const key = getKey(elem)

    if (!acc[key]) acc[key] = []
    acc[key].push(elem)

    return acc
  }, {} as Record<string, V[]>)

  return map
}

/**
 * `:`로 구분되는 path의 parameter를 실제 값으로 변환한 path를 만듭니다.
 * @param path `/some/path/:id` 형식의 path
 * @param params {id:"example_id"} 형식의 parameters
 */
export const generatePath = <Path extends string>(path: Path, params: Record<PathParam<Path>, string>) => {
  let result: string = path
  objectKeys(params).forEach(key => {
    result = result.replace(`:${key}`, params[key])
  })
  return result
}

export const isValidHttpUrl = (string: string) => {
  let url

  try {
    url = new URL(string)
  } catch (error) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

/**
 * 마이플레이스 내부 URL인지 확인
 * @param url
 * @returns 내부 라우팅이면 true 그렇지 않으면(외부 접근) false
 */
export const isInternalRouting = (url: string) => {
  try {
    return url.includes(routes.ORIGIN + BASE_PATH)
  } catch (error) {
    return false
  }
}
export const generateRandomKey = (): string =>
  Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

const storageKeys = {
  usedFollowing: 'usedFollowing-{userId}',
  openMissionAlert: 'openMissionAlert-{userId}',
  openMissionInfo: 'openMissionInfo-{userId}',
  openMissionRewardPopup: 'openMissionRewardPopup-{userId}-{missionId}',
  openMissionCompleteGuide: 'openMissionCompleteGuide-{userId}',
  openRewardGuide: 'openRewardGuide-{yearMonth}',
  showVisitCardMenuTooltip: 'showVisitCardMenuTooltip-{userId}',
  onboarding: 'onboarding_{userId}',
  closeTakeStampTooltip: 'closeTakeStampTooltip-{userId}',
  closeVisitPrivateInfoLayer: 'closeVisitPrivateInfoLayer',
} as const

/**
 * @param key 로컬 스토리지 key
 * @param params key에 인자(ex. `seomthig-{id}`)가 있다면, 넣을 값 {id:...}
 */
export const browserStorage = <Key extends keyof typeof storageKeys>(
  ...args: StringParams<(typeof storageKeys)[Key]> extends never
    ? [Key]
    : [Key, Record<StringParams<(typeof storageKeys)[Key]>, string>]
) => {
  const [key, params] = args
  let _key: string = storageKeys[key]
  if (params)
    objectKeys(params).forEach(key => {
      _key = _key.replace(`{${key}}`, params[key])
    })

  return {
    get: () => {
      if (IS_BROWSER) return localStorage.getItem(_key)
      return null
    },
    set: (value: string) => {
      if (IS_BROWSER) localStorage.setItem(_key, value)
    },
  }
}

export const getHashFromRouter = (router: NextRouter) => {
  const hash = router.asPath.split('#')[1]
  if (!hash) return undefined
  return '#' + hash
}

export const callPhone = (phoneNumber: string): void => {
  const anchor = document.createElement('a')
  anchor.setAttribute('href', `tel:${phoneNumber}`)
  anchor.click()
}

export const updateURLQueryString = (url: string, params: Record<string | number, string | number>) => {
  const parsedUrl = new URL(url)

  objectKeys(params).forEach(key => {
    parsedUrl.searchParams.set(key.toString(), params[key].toString())
  })

  return parsedUrl.toString()
}

export const clearMobileCacheStorage = () => {
  Object.entries(window.sessionStorage).forEach(([key, value]) => {
    if (value.includes('ApolloCache')) {
      sessionStorage.removeItem(key)
    }
  })
}

export const generateIntegerByKey = ({ key, min = 0, max }: { key: string; min?: number; max: number }) => {
  const parsedNum = parseInt(key.slice(-2), 16)
  return (parsedNum % max) + min
}

export const isValidObjectId = (objectId: string): boolean => {
  const objectIdRegex = /^[0-9a-f]{24}$/
  return objectIdRegex.test(objectId)
}

export const getPathWithQueryString = ({
  query,
  replace = false,
}: {
  query: Record<string, string>
  replace?: boolean
}) => {
  if (replace) {
    const queryString = qs.stringify(query)
    return `${location.pathname}?${queryString}`
  }

  const existingQuery = new URLSearchParams(location.search)
  Object.keys(query).forEach(key => {
    existingQuery.set(key, query[key])
  })

  return `${location.pathname}?${existingQuery.toString()}${location.hash}`
}

export const getVersioningTimeId = (durationMinutes: number) => {
  const nowUnixStampMilliseconds = new Date().getTime()

  return Math.floor(nowUnixStampMilliseconds / (durationMinutes * 60 * 1000))
}

export const isReviewGroupsMediaFilter = (mediaFilter?: any): mediaFilter is ReviewGroupsMediaFilter => {
  return !!Object.values(ReviewGroupsMediaFilter).includes(mediaFilter)
}

export const isUserReviewGroupsSort = (sort: any): sort is UserReviewGroupsSort => {
  return !!Object.values(UserReviewGroupsSort).includes(sort)
}

export const removeQueryStringWithoutRouterChange = (key: string) => {
  if (!IS_BROWSER) return

  let searchParams = new URLSearchParams(window.location.search)
  searchParams.delete(key)

  const newUrl = window.location.origin + window.location.pathname + '?' + searchParams.toString()
  history.replaceState(history.state, '', newUrl)
}

export const createWordWithEllipsis = (word: string, maxLength: number) => {
  if (word.length <= maxLength) return word

  return word.slice(0, maxLength) + '...'
}

export const generateQueryString = (queryObject: Record<any, any>) => {
  const KEEP_QUERY_PARAM_KEYS = ['from', 'napp', 'referer', 'search', 'rtab', 'regionCode']
  return Object.entries(queryObject)
    .reduce((acc, [key, value]) => {
      if (KEEP_QUERY_PARAM_KEYS.includes(key)) {
        acc.append(key.toString(), value?.toString() ?? '')
      }
      return acc
    }, new URLSearchParams())
    .toString()
}

export const splitString = (str: string, delimiter: string = '') => {
  // toArray사용시 이모지도 한글자로 분리할 수 있음
  const words = delimiter === '' ? toArray(str) : str.split(delimiter)

  return words
}

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

const encodeArrayBufferToBase64UrlWithoutPadding = (arraybuffer: ArrayBuffer) => {
  const lookup = new Uint8Array(256)
  for (var i = 0; i < CHARS.length; i++) {
    lookup[CHARS.charCodeAt(i)] = i
  }
  const bytes = new Uint8Array(arraybuffer)
  const len = bytes.length
  let base64 = ''

  for (i = 0; i < len; i += 3) {
    base64 += CHARS[bytes[i] >> 2]
    base64 += CHARS[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)]
    base64 += CHARS[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)]
    base64 += CHARS[bytes[i + 2] & 63]
  }

  if (len % 3 === 2) {
    base64 = base64.substring(0, base64.length - 1)
  } else if (len % 3 === 1) {
    base64 = base64.substring(0, base64.length - 2)
  }

  return base64
}

export const getHashedIdnoForMapAppBridge = (idno: string) => {
  const hashedIdno = sha512.arrayBuffer(idno)
  const base64Idno = encodeArrayBufferToBase64UrlWithoutPadding(hashedIdno)
  return base64Idno
}
